/**
 * GeoLibre project catalog for the Kaarten & Bibliotheek page.
 *
 * Contract (written by the map pipeline, served by the GeoLibre app behind the gate):
 *   GET /map/data/project/index.json
 *   { "projects": [ { "id": "sb-huisstijl", "name": "…", "description": "…",
 *                     "file": "sb-huisstijl.geolibre.json", "updated": "2026-10-13", "tags": ["huisstijl"] } ] }
 * Required per project: id, name, file (a bare *.json file name in /map/data/project/).
 * Optional: description, updated (YYYY-MM-DD), tags (string[]),
 *           image ("previews/<name>.webp|png|jpg", a GeoLibre screenshot made by the map pipeline),
 *           group (section id: basiskaart | themakaarten | projecten; without it the tags decide),
 *           layers (number of layers, shown on the card),
 *           root (2026-11-04: a folder below /map/data/, e.g. "staging": the card opens /map/data/<root>/project/<file>;
 *                 the registry catalog of pipeline v2 on the staging mount; without it /map/data/project/).
 * Invalid entries are skipped (an invalid image or group is dropped, the entry stays);
 * no valid entry, a missing file or a login redirect = the hard-coded fallback below.
 */
export interface MapProject {
  id: string;
  name: string;
  description?: string;
  file: string;
  updated?: string;
  tags?: string[];
  image?: string;
  group?: string;
  layers?: number;
  root?: string;
}

export const PROJECT_INDEX_URL = '/map/data/project/index.json';

const FILE_RE = /^[A-Za-z0-9._-]+\.json$/;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const IMG_RE = /^previews\/[A-Za-z0-9._-]+\.(webp|png|jpe?g)$/;
const GROUP_RE = /^[a-z0-9-]+$/;
const ROOT_RE = /^[a-z0-9_-]+$/;

/** Sections of the catalog, in page order; a group not listed here gets its own section after these. */
export const MAP_GROUPS: { id: string; title: string; hint: string }[] = [
  { id: 'basiskaart', title: 'Basiskaart', hint: 'De SB-huisstijl als ondergrond, op vier schalen.' },
  { id: 'themakaarten', title: 'Themakaarten', hint: 'Landelijke kaartseries uit sb_production, in de opgeslagen QGIS-stijl.' },
  { id: 'projecten', title: 'Projectkaarten', hint: 'Kaarten van één project of opdracht.' },
  {
    id: 'register',
    title: 'Datacatalogus per thema',
    hint: 'Uit het register (staging): elke laag op de SB-basiskaart, met download per laag waar dat mag.',
  },
];

export const FALLBACK_PROJECTS: MapProject[] = [
  {
    id: 'sb-huisstijl',
    name: 'SB huisstijl (NL, provincie, gemeente, lokaal)',
    description: 'De SB-basiskaart in huisstijl op vier schaalniveaus.',
    file: 'sb-huisstijl.geolibre.json',
    tags: ['huisstijl'],
    group: 'basiskaart',
  },
  {
    id: 'sb-tilburg',
    name: 'Tilburg',
    description: 'Projectkaart Tilburg.',
    file: 'sb-tilburg.geolibre.json',
    tags: ['project'],
    group: 'projecten',
  },
];

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined;

/** Validated projects from the parsed index.json; [] when nothing usable is in it. */
export function parseProjectIndex(data: unknown): MapProject[] {
  const list = (data as { projects?: unknown } | null)?.projects;
  if (!Array.isArray(list)) return [];
  const seen = new Set<string>();
  const out: MapProject[] = [];
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const id = str(r.id);
    const name = str(r.name);
    const file = str(r.file);
    if (!id || !name || !file || !FILE_RE.test(file) || file.includes('..') || seen.has(id)) continue;
    seen.add(id);
    const updated = str(r.updated);
    const image = str(r.image);
    const group = str(r.group);
    const root = str(r.root);
    out.push({
      id,
      name,
      file,
      description: str(r.description),
      updated: updated && DATE_RE.test(updated) ? updated : undefined,
      tags: Array.isArray(r.tags) ? r.tags.filter((t): t is string => typeof t === 'string' && t !== '') : undefined,
      image: image && IMG_RE.test(image) ? image : undefined,
      group: group && GROUP_RE.test(group) ? group : undefined,
      layers: typeof r.layers === 'number' && Number.isInteger(r.layers) && r.layers > 0 ? r.layers : undefined,
      root: root && ROOT_RE.test(root) ? root : undefined,
    });
  }
  return out;
}

/** Folder of a project's file and preview: /map/data/project/ or, with a root, /map/data/<root>/project/. */
export function projectDir(root?: string): string {
  return root && ROOT_RE.test(root) ? `/map/data/${root}/project/` : '/map/data/project/';
}

/** GeoLibre link that opens one project file. */
export function mapUrl(file: string, origin: string, root?: string): string {
  return `/map/?url=${origin}${projectDir(root)}${file}`;
}

/** Preview image of a project (same origin, behind the same gate as the map). */
export function imageUrl(image: string, root?: string): string {
  return `${projectDir(root)}${image}`;
}

/** Downloads pages written by the map pipeline (publish.sh): every layer's GeoParquet link or the reason there is none. */
export const DOWNLOADS_URL = '/map/data/project/downloads.html';
export const STAGING_DOWNLOADS_URL = '/map/data/staging/project/downloads.html';

/** Section of a project: its group, else its tags (huisstijl -> basiskaart, themakaart -> themakaarten), else projecten. */
export function groupOf(p: MapProject): string {
  if (p.group) return p.group;
  if (p.tags?.includes('huisstijl')) return 'basiskaart';
  if (p.tags?.includes('themakaart')) return 'themakaarten';
  return 'projecten';
}

/** Case-insensitive match on name, description and tags; an empty query matches everything. */
export function matchesQuery(p: MapProject, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [p.name, p.description ?? '', ...(p.tags ?? [])].some((s) => s.toLowerCase().includes(q));
}

export interface MapGroup {
  id: string;
  title: string;
  hint: string;
  projects: MapProject[];
}

/** Projects per section, sections in MAP_GROUPS order (unknown groups after them); within a section the index
 *  order, with maps tagged 'voorbeeld' (no canonical style yet) last. Empty sections are left out. */
export function groupProjects(projects: MapProject[]): MapGroup[] {
  const by = new Map<string, MapProject[]>();
  for (const p of projects) {
    const g = groupOf(p);
    by.set(g, [...(by.get(g) ?? []), p]);
  }
  const known = MAP_GROUPS.map((g) => g.id);
  const ids = [...known, ...[...by.keys()].filter((g) => !known.includes(g))];
  const isPreview = (p: MapProject) => (p.tags?.includes('voorbeeld') ? 1 : 0);
  return ids
    .filter((id) => by.has(id))
    .map((id) => {
      const meta = MAP_GROUPS.find((g) => g.id === id);
      const list = by.get(id) ?? [];
      return {
        id,
        title: meta?.title ?? id,
        hint: meta?.hint ?? '',
        projects: list.map((p, i) => ({ p, i })).sort((a, b) => isPreview(a.p) - isPreview(b.p) || a.i - b.i).map((x) => x.p),
      };
    });
}

/** "2026-10-13" -> "13-10-2026". */
export function formatDate(iso: string): string {
  const m = DATE_RE.exec(iso);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso;
}
